import { useEffect, useState } from 'react'
import { CalendarDays, MessageCircle, X, Newspaper, ArrowLeft, ArrowRight } from 'lucide-react'
import { useBlogStore } from '../stores'
import { useBlogFixtureStore } from '../stores/useBlogFixtureStore'

const buildPostShareUrl = (postId?: string | null) => {
  const url = new URL(window.location.origin)
  url.pathname = postId ? `/blog/${encodeURIComponent(postId)}` : '/blog'
  return url.toString()
}

const buildSocialPreviewSvg = (title: string, summary: string) => {
  const safeTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const safeSummary = summary.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="630" viewBox="0 0 1200 630">
      <defs>
        <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#07111a"/>
          <stop offset="100%" stop-color="#0d1f2a"/>
        </linearGradient>
      </defs>
      <rect width="1200" height="630" fill="url(#bg)"/>
      <rect x="88" y="98" width="1024" height="434" rx="28" fill="rgba(15, 23, 42, 0.82)" stroke="rgba(34, 197, 94, 0.6)"/>
      <text x="135" y="220" fill="#86efac" font-family="Arial, Helvetica, sans-serif" font-size="32" font-weight="700" letter-spacing="4">SCOUTA NEWS</text>
      <text x="135" y="310" fill="#f8fafc" font-family="Arial, Helvetica, sans-serif" font-size="62" font-weight="800">${safeTitle}</text>
      <text x="135" y="395" fill="#cbd5e1" font-family="Arial, Helvetica, sans-serif" font-size="28" font-weight="500">${safeSummary}</text>
      <circle cx="968" cy="152" r="52" fill="#22c55e" opacity="0.9"/>
      <text x="947" y="171" fill="#07111a" font-family="Arial, Helvetica, sans-serif" font-size="30" font-weight="900">S</text>
    </svg>
  `)}`
}

function BlogPage() {
  const posts = useBlogStore((state) => state.posts.filter((post) => post.is_published))
  const fixtures = useBlogFixtureStore((state) => state.fixtures)

  const getPostIdFromUrl = () => {
    const url = new URL(window.location.href)
    const cleanedPath = url.pathname.replace(/^\/blog\/?/, '').replace(/^\//, '')
    if (cleanedPath && cleanedPath !== 'blog') return decodeURIComponent(cleanedPath)
    return url.searchParams.get('post')
  }

  const [selectedPostId, setSelectedPostId] = useState<string | null>(() => getPostIdFromUrl())
  const selectedPost = posts.find((p) => p.id === selectedPostId)

  useEffect(() => {
    const syncRoute = (postId: string | null) => {
      const url = new URL(window.location.href)
      url.hash = ''
      if (postId) {
        url.pathname = `/blog/${encodeURIComponent(postId)}`
        url.search = ''
      } else {
        url.pathname = '/blog'
        url.search = ''
      }
      window.history.replaceState({}, '', url.toString())
    }

    syncRoute(selectedPostId)
  }, [selectedPostId])

  useEffect(() => {
    if (!posts.length) return
    const currentFromUrl = getPostIdFromUrl()
    if (currentFromUrl && !selectedPostId) {
      setSelectedPostId(currentFromUrl)
    }
    if (!currentFromUrl && selectedPostId) {
      setSelectedPostId(null)
    }
  }, [posts, selectedPostId])

  useEffect(() => {
    const targetPost = selectedPost || posts[0]
    const pageTitle = targetPost ? `${targetPost.title} | Scouta Blog` : 'Scouta Blog & Updates'
    const pageDescription = targetPost ? (targetPost.summary || 'Scouta football updates and blog coverage.') : 'Scouta blog, fixtures and football updates from Nigeria.'
    const coverImage = targetPost
      ? (targetPost.cover_image_url || extractFirstImageSrc(targetPost.content) || buildSocialPreviewSvg(targetPost.title, targetPost.summary || 'Scouta blog post'))
      : buildSocialPreviewSvg('Scouta Blog', 'Fixture updates and football stories from Nigeria')

    document.title = pageTitle

    const setMeta = (selector: string, attribute: string, value: string) => {
      let element = document.head.querySelector(selector) as HTMLMetaElement | null
      if (!element) {
        element = document.createElement('meta')
        document.head.appendChild(element)
      }
      element.setAttribute(attribute, value)
    }

    setMeta('meta[name="description"]', 'content', pageDescription)
    setMeta('meta[property="og:title"]', 'content', pageTitle)
    setMeta('meta[property="og:description"]', 'content', pageDescription)
    setMeta('meta[property="og:image"]', 'content', coverImage)
    setMeta('meta[property="og:url"]', 'content', buildPostShareUrl(targetPost?.id || null))
    setMeta('meta[property="og:type"]', 'content', 'article')
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image')
    setMeta('meta[name="twitter:title"]', 'content', pageTitle)
    setMeta('meta[name="twitter:description"]', 'content', pageDescription)
    setMeta('meta[name="twitter:image"]', 'content', coverImage)
  }, [posts, selectedPost])

  const shareOnWhatsApp = (title: string, postId?: string | null) => {
    const shareUrl = buildPostShareUrl(postId || null)
    return `https://wa.me/?text=${encodeURIComponent(`${title} - Read the latest on Scouta: ${shareUrl}`)}`
  }

  const shareOnX = (title: string, postId?: string | null) => {
    const shareUrl = buildPostShareUrl(postId || null)
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${title} on Scouta`)}&url=${encodeURIComponent(shareUrl)}`
  }

  const upcomingFixtures = fixtures.filter((fixture) => fixture.status === 'upcoming').slice(0, 5)
  const recentResults = fixtures.filter((fixture) => fixture.status === 'played').slice(0, 5)

  const extractFirstImageSrc = (html: string | undefined) => {
    if (!html) return null
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const img = doc.querySelector('img')
      return img?.getAttribute('src') || null
    } catch (e) {
      return null
    }
  }

  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="max-w-7xl mx-auto px-4 py-14">
        <div className="rounded-3xl border border-white/5 bg-[#091116] p-10 shadow-xl shadow-green-500/10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-4 max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-green-600/10 px-4 py-2 text-xs uppercase tracking-[0.3em] text-green-400">
                <CalendarDays size={16} /> Blog & Fixtures
              </div>
              <h1 className="text-5xl font-black tracking-tight text-white">Scouta Newsroom</h1>
              <p className="max-w-2xl text-lg text-slate-300">
                Follow Nigerian league fixtures, scouting stories, and talent updates. Blog posts are public and shareable, with live fixture summaries for scouts and clubs.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <a
                href={shareOnWhatsApp('Scouta Blog', selectedPost?.id || null)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-800 bg-[#0c151b] px-5 py-3 text-sm text-white transition hover:border-green-500 hover:text-green-300"
              >
                <MessageCircle size={18} /> Share on WhatsApp
              </a>
              <a
                href={shareOnX('Scouta Blog', selectedPost?.id || null)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-800 bg-[#0c151b] px-5 py-3 text-sm text-white transition hover:border-green-500 hover:text-green-300"
              >
                <X size={18} /> Share on X
              </a>
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[0.75fr_0.45fr] mt-10">
          <section className="space-y-6">
            {selectedPost ? (
              <article className="rounded-3xl border border-white/5 bg-[#0c151b] p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <button 
                  onClick={() => setSelectedPostId(null)}
                  className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-green-400 hover:text-green-300 transition bg-green-900/10 px-4 py-2 rounded-full border border-green-800/30"
                >
                  <ArrowLeft size={16} /> Back to all posts
                </button>
                
                <div className="flex flex-col gap-3">
                  <p className="text-xs uppercase tracking-[0.28em] text-green-400">{new Date(selectedPost.published_at).toLocaleDateString()}</p>
                  <h2 className="text-4xl font-black text-white leading-tight">{selectedPost.title}</h2>
                </div>
                
                {selectedPost.tags && selectedPost.tags.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                    {selectedPost.tags.map((tag) => (
                      <span key={tag} className="rounded-full border border-slate-700 px-3 py-1 uppercase tracking-[0.2em] text-slate-300">{tag}</span>
                    ))}
                  </div>
                )}
                
                <p className="mt-8 text-lg text-slate-300 leading-relaxed font-medium pb-8 border-b border-gray-800">{selectedPost.summary}</p>
                
                {/* HTML content rendering (supports embedded <img>) */}
                <div 
                  className="mt-8 text-slate-300 whitespace-pre-wrap leading-loose overflow-hidden [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-2xl [&_img]:my-8 [&_img]:border [&_img]:border-gray-800"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />

                <div className="mt-12 pt-8 border-t border-gray-800 flex flex-wrap gap-3">
                  <a
                    href={shareOnWhatsApp(selectedPost.title, selectedPost.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-gray-800 bg-green-600/10 px-4 py-2 text-sm text-green-300 transition hover:bg-green-600/15"
                  >
                    <MessageCircle size={16} /> Share
                  </a>
                  <a
                    href={shareOnX(selectedPost.title, selectedPost.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-gray-800 bg-slate-900 px-4 py-2 text-sm text-white transition hover:border-green-500"
                  >
                    <X size={16} /> Share on X
                  </a>
                </div>
              </article>
            ) : (
              posts.map((post) => {
                const thumbnail = extractFirstImageSrc(post.content)
                return (
                  <article key={post.id} className="rounded-3xl border border-white/5 bg-[#0c151b] p-8 transition hover:border-green-500/30">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex gap-4 sm:items-start">
                        {thumbnail && (
                          <img src={thumbnail} alt="thumbnail" className="w-full max-w-md sm:w-48 h-32 object-cover rounded-2xl border border-gray-800" />
                        )}
                        <div>
                          <p className="text-xs uppercase tracking-[0.28em] text-green-400">{new Date(post.published_at).toLocaleDateString()}</p>
                          <h2 className="mt-2 text-3xl font-bold text-white">{post.title}</h2>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                        {post.tags?.map((tag) => (
                          <span key={tag} className="rounded-full border border-slate-700 px-3 py-1 uppercase tracking-[0.2em] text-slate-300">{tag}</span>
                        ))}
                      </div>
                    </div>
                    <p className="mt-4 text-slate-400 leading-7">{post.summary}</p>
                    
                    <button 
                      onClick={() => setSelectedPostId(post.id)}
                      className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-white hover:text-green-400 transition"
                    >
                      Read more <ArrowRight size={16} />
                    </button>
                  </article>
                )
              })
            )}
          </section>

          <aside className="space-y-6">
            <div className="rounded-3xl border border-white/5 bg-[#0c151b] p-6">
              <div className="flex items-center gap-3 text-green-400">
                <Newspaper size={20} />
                <h3 className="text-lg font-bold">Latest Fixtures</h3>
              </div>
              <div className="mt-5 space-y-3 text-sm text-slate-300">
                {upcomingFixtures.length === 0 ? (
                  <p>No fixtures available right now.</p>
                ) : (
                  upcomingFixtures.map((fixture) => (
                    <div key={fixture.id} className="rounded-2xl border border-gray-800 bg-slate-950/40 p-4">
                      <p className="text-sm text-green-300 font-semibold">{fixture.date}</p>
                      <p className="text-base font-semibold text-white">{fixture.home_team} vs {fixture.away_team}</p>
                      <p className="text-xs text-slate-400">{fixture.competition} • {fixture.location}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-white/5 bg-[#0c151b] p-6">
              <div className="flex items-center gap-3 text-green-400">
                <CalendarDays size={20} />
                <h3 className="text-lg font-bold">Recent Results</h3>
              </div>
              <div className="mt-5 space-y-3 text-sm text-slate-300">
                {recentResults.length === 0 ? (
                  <p>No results have been posted yet.</p>
                ) : (
                  recentResults.map((fixture) => (
                    <div key={fixture.id} className="rounded-2xl border border-gray-800 bg-slate-950/40 p-4">
                      <p className="text-sm text-green-300 font-semibold">{fixture.date}</p>
                      <p className="text-base font-semibold text-white">{fixture.home_team} {fixture.score} {fixture.away_team}</p>
                      <p className="text-xs text-slate-400">{fixture.competition}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default BlogPage
